/**
 * useScrollDepth Hook
 * Tracks scroll depth milestones (25%, 50%, 75%, 90%)
 *
 * @module hooks/useScrollDepth
 * @description Automatically tracks scroll depth for engagement metrics
 *
 * @example
 * ```tsx
 * // In a page component
 * useScrollDepth();
 * ```
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackScrollDepth } from "@/lib/analytics";

/**
 * A single "read it" milestone rather than 25/50/75/90.
 *
 * Four milestones fired up to four events per pageview and answered no question worth
 * the volume — a 25% scroll is a thumb twitch. One deep-scroll signal still separates
 * "read the page" from "bounced", which matters most on the generated SEO landing pages
 * where thin content is the risk. GA4's own automatic scroll event covers 90%.
 */
const MILESTONES = [75];

/** Debounce delay in milliseconds */
const DEBOUNCE_DELAY = 100;

export const useScrollDepth = (): void => {
  const trackedMilestones = useRef<Set<number>>(new Set());
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset tracked milestones on route change
  useEffect(() => {
    trackedMilestones.current.clear();
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      // Debounce scroll events
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const scrollTop = window.scrollY;
        const docHeight =
          document.documentElement.scrollHeight - window.innerHeight;

        // Prevent division by zero
        if (docHeight <= 0) return;

        const scrollPercent = Math.round((scrollTop / docHeight) * 100);

        MILESTONES.forEach((milestone) => {
          if (
            scrollPercent >= milestone &&
            !trackedMilestones.current.has(milestone)
          ) {
            trackedMilestones.current.add(milestone);
            trackScrollDepth(milestone);
          }
        });
      }, DEBOUNCE_DELAY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location.pathname]);
};

export default useScrollDepth;
